"use client";

import { c as _c } from "react/compiler-runtime";
import type { Category } from "@spree/sdk";
import { ArrowLeft, Check, ChevronRight, User, X } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetTitle } from "@/components/ui/sheet";
import { useStore } from "@/contexts/StoreContext";
import { useCountrySwitch } from "@/hooks/useCountrySwitch";
import { getDomainMarkets } from "@/lib/domain/host-map";
type PanelType = {
  kind: "main";
} | {
  kind: "category";
  category: Category;
} | {
  kind: "pages";
} | {
  kind: "country";
};
interface NavLink {
  slug: string;
  title: string;
}
interface MobileMenuProps {
  rootCategories: Category[];
  pages: NavLink[];
  blogs: NavLink[];
  basePath: string;
}
export function MobileMenu(t0) {
  const $ = _c(201);
  const {
    rootCategories,
    pages,
    blogs,
    basePath
  } = t0;
  const t = useTranslations("header");
  const [open, setOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      kind: "main"
    }];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [panelStack, setPanelStack] = useState(t1);
  const [animatedIndex, setAnimatedIndex] = useState(0);
  const rafRef = useRef(null);
  const timeoutRef = useRef(null);
  const {
    country,
    currency
  } = useStore();
  const {
    regionHref
  } = useCountrySwitch();
  let T0;
  let T1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== animatedIndex || $[2] !== basePath || $[3] !== blogs.length || $[4] !== country || $[5] !== currency || $[6] !== hasInteracted || $[7] !== open || $[8] !== pages || $[9] !== panelStack || $[10] !== regionHref || $[11] !== rootCategories || $[12] !== t) {
    const regions = getDomainMarkets();
    let t19;
    if ($[32] !== country) {
      t19 = r => r.country === country.toLowerCase();
      $[32] = country;
      $[33] = t19;
    } else {
      t19 = $[33];
    }
    const currentRegion = regions.find(t19);
    const currentPanel = panelStack[panelStack.length - 1];
    let t20;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = () => {
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
      $[34] = t20;
    } else {
      t20 = $[34];
    }
    const cancelPendingCallbacks = t20;
    let t21;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = panel => {
        cancelPendingCallbacks();
        flushSync(() => {
          setPanelStack(prev => [...prev, panel]);
        });
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          setAnimatedIndex(_temp);
        });
      };
      $[35] = t21;
    } else {
      t21 = $[35];
    }
    const pushPanel = t21;
    let t22;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = () => {
        cancelPendingCallbacks();
        setAnimatedIndex(_temp2);
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;
          setPanelStack(_temp3);
        }, 300);
      };
      $[36] = t22;
    } else {
      t22 = $[36];
    }
    const popPanel = t22;
    let t23;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = value => {
        setOpen(value);
        if (!value) {
          cancelPendingCallbacks();
          setPanelStack([{
            kind: "main"
          }]);
          setAnimatedIndex(0);
        }
      };
      $[37] = t23;
    } else {
      t23 = $[37];
    }
    const handleOpenChange = t23;
    let t24;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = new Set(["about-us", "contact-us"]);
      $[38] = t24;
    } else {
      t24 = $[38];
    }
    const MAIN_NAV_SLUGS = t24;
    let t25;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = p => !MAIN_NAV_SLUGS.has(p.slug);
      $[39] = t25;
    } else {
      t25 = $[39];
    }
    const guidePages = pages.filter(t25);
    let t26;
    if ($[40] !== pages) {
      t26 = pages.some(_temp4);
      $[40] = pages;
      $[41] = t26;
    } else {
      t26 = $[41];
    }
    const hasAbout = t26;
    const hasContactPage = pages.some(_temp5);
    T1 = Sheet;
    t16 = open;
    t17 = handleOpenChange;
    let t27;
    if ($[42] !== hasInteracted || $[43] !== open) {
      t27 = () => {
        if (!hasInteracted) {
          setHasInteracted(true);
        }
        setOpen(!open);
      };
      $[42] = hasInteracted;
      $[43] = open;
      $[44] = t27;
    } else {
      t27 = $[44];
    }
    let t28;
    if ($[45] !== open || $[46] !== t) {
      t28 = open ? t("closeMenu") : t("openMenu");
      $[45] = open;
      $[46] = t;
      $[47] = t28;
    } else {
      t28 = $[47];
    }
    const t29 = `absolute left-0 right-0 h-0.5 bg-current rounded-full top-[2px] ${hasInteracted ? open ? "animate-hamburger-top-open" : "animate-hamburger-top-close" : ""}`;
    let t30;
    if ($[48] !== t29) {
      t30 = <span className={t29} />;
      $[48] = t29;
      $[49] = t30;
    } else {
      t30 = $[49];
    }
    const t31 = `absolute left-0 right-0 h-0.5 bg-current rounded-full top-1/2 -translate-y-1/2 ${hasInteracted ? open ? "animate-hamburger-mid-open" : "animate-hamburger-mid-close" : ""}`;
    let t32;
    if ($[50] !== hasInteracted || $[51] !== open) {
      t32 = hasInteracted && !open ? {
        animationDelay: "0.2s",
        opacity: 0
      } : undefined;
      $[50] = hasInteracted;
      $[51] = open;
      $[52] = t32;
    } else {
      t32 = $[52];
    }
    let t33;
    if ($[53] !== t31 || $[54] !== t32) {
      t33 = <span className={t31} style={t32} />;
      $[53] = t31;
      $[54] = t32;
      $[55] = t33;
    } else {
      t33 = $[55];
    }
    const t34 = `absolute left-0 right-0 h-0.5 bg-current rounded-full bottom-[2px] ${hasInteracted ? open ? "animate-hamburger-bottom-open" : "animate-hamburger-bottom-close" : ""}`;
    let t35;
    if ($[56] !== t34) {
      t35 = <span className={t34} />;
      $[56] = t34;
      $[57] = t35;
    } else {
      t35 = $[57];
    }
    let t36;
    if ($[58] !== t30 || $[59] !== t33 || $[60] !== t35) {
      t36 = <div className="relative w-5 h-5">{t30}{t33}{t35}</div>;
      $[58] = t30;
      $[59] = t33;
      $[60] = t35;
      $[61] = t36;
    } else {
      t36 = $[61];
    }
    if ($[62] !== t27 || $[63] !== t28 || $[64] !== t36) {
      t18 = <Button variant="ghost" size="icon-lg" onClick={t27} aria-label={t28} className="relative z-[60] cursor-pointer">{t36}</Button>;
      $[62] = t27;
      $[63] = t28;
      $[64] = t36;
      $[65] = t18;
    } else {
      t18 = $[65];
    }
    T0 = SheetContent;
    t10 = "left";
    t11 = "flex flex-col !gap-0 !rounded-none overflow-hidden max-md:!top-16 max-md:!h-[calc(100%-4rem)] max-md:!w-full max-md:!max-w-none max-md:!border-r-0";
    t12 = false;
    t13 = "max-md:!top-16 max-md:!bg-transparent";
    let t37;
    if ($[66] !== t) {
      t37 = t("menu");
      $[66] = t;
      $[67] = t37;
    } else {
      t37 = $[67];
    }
    if ($[68] !== t37) {
      t14 = <SheetTitle className="sr-only">{t37}</SheetTitle>;
      $[68] = t37;
      $[69] = t14;
    } else {
      t14 = $[69];
    }
    const t38 = `text-base font-semibold transition-all duration-300 ease-in-out absolute left-4 ${currentPanel.kind === "main" ? "translate-x-0 opacity-100" : "-translate-x-8 opacity-0 pointer-events-none"}`;
    let t39;
    if ($[70] !== t) {
      t39 = t("menu");
      $[70] = t;
      $[71] = t39;
    } else {
      t39 = $[71];
    }
    let t40;
    if ($[72] !== t38 || $[73] !== t39) {
      t40 = <span className={t38}>{t39}</span>;
      $[72] = t38;
      $[73] = t39;
      $[74] = t40;
    } else {
      t40 = $[74];
    }
    const t41 = `flex items-center gap-2 text-gray-700 hover:text-gray-900 text-base font-semibold cursor-pointer transition-all duration-300 ease-in-out absolute left-4 ${currentPanel.kind !== "main" ? "translate-x-0 opacity-100" : "translate-x-8 opacity-0 pointer-events-none"}`;
    let t42;
    if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
      t42 = <ArrowLeft className="w-5 h-5" />;
      $[75] = t42;
    } else {
      t42 = $[75];
    }
    let t43;
    if ($[76] !== currentPanel.category || $[77] !== currentPanel.kind || $[78] !== t) {
      t43 = currentPanel.kind === "category" ? currentPanel.category.name : currentPanel.kind === "country" ? t("selectCountry") : currentPanel.kind === "pages" ? "Guides" : "";
      $[76] = currentPanel.category;
      $[77] = currentPanel.kind;
      $[78] = t;
      $[79] = t43;
    } else {
      t43 = $[79];
    }
    let t44;
    if ($[80] !== t43) {
      t44 = <span>{t43}</span>;
      $[80] = t43;
      $[81] = t44;
    } else {
      t44 = $[81];
    }
    let t45;
    if ($[82] !== t41 || $[83] !== t44) {
      t45 = <button type="button" onClick={popPanel} className={t41}>{t42}{t44}</button>;
      $[82] = t41;
      $[83] = t44;
      $[84] = t45;
    } else {
      t45 = $[84];
    }
    let t46;
    if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
      t46 = () => setOpen(false);
      $[85] = t46;
    } else {
      t46 = $[85];
    }
    let t47;
    if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
      t47 = <Button variant="ghost" size="icon-sm" onClick={t46} className="cursor-pointer ml-auto"><X className="size-4" /></Button>;
      $[86] = t47;
    } else {
      t47 = $[86];
    }
    if ($[87] !== t40 || $[88] !== t45) {
      t15 = <div className="hidden md:flex items-center justify-between px-4 h-16 border-b border-gray-200 relative overflow-hidden">{t40}{t45}{t47}</div>;
      $[87] = t40;
      $[88] = t45;
      $[89] = t15;
    } else {
      t15 = $[89];
    }
    t6 = "relative flex-1 overflow-hidden";
    const t48 = `absolute inset-0 flex flex-col bg-white transition-transform duration-300 ease-in-out ${animatedIndex === 0 && currentPanel.kind === "main" ? "translate-x-0" : "-translate-x-full"}`;
    const t49 = basePath || "/";
    let t50;
    if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
      t50 = () => setOpen(false);
      $[90] = t50;
    } else {
      t50 = $[90];
    }
    let t51;
    if ($[91] !== t) {
      t51 = t("home");
      $[91] = t;
      $[92] = t51;
    } else {
      t51 = $[92];
    }
    let t52;
    if ($[93] !== t49 || $[94] !== t51) {
      t52 = <Link href={t49} onClick={t50} className="text-left text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg px-3 py-2.5 text-base transition-colors">{t51}</Link>;
      $[93] = t49;
      $[94] = t51;
      $[95] = t52;
    } else {
      t52 = $[95];
    }
    const t53 = `${basePath}/products`;
    let t54;
    if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
      t54 = () => setOpen(false);
      $[96] = t54;
    } else {
      t54 = $[96];
    }
    let t55;
    if ($[97] !== t) {
      t55 = t("allProducts");
      $[97] = t;
      $[98] = t55;
    } else {
      t55 = $[98];
    }
    let t56;
    if ($[99] !== t53 || $[100] !== t55) {
      t56 = <Link href={t53} onClick={t54} className="text-left text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg px-3 py-2.5 text-base transition-colors">{t55}</Link>;
      $[99] = t53;
      $[100] = t55;
      $[101] = t56;
    } else {
      t56 = $[101];
    }
    const t57 = guidePages.length > 0 && <button type="button" onClick={() => pushPanel({
      kind: "pages"
    })} className="flex items-center justify-between w-full text-left text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg px-3 py-2.5 text-base transition-colors"><span>Guides</span><ChevronRight className="w-4 h-4 text-gray-400" /></button>;
    let t58;
    if ($[102] !== basePath || $[103] !== blogs.length) {
      t58 = blogs.length > 0 && <Link href={`${basePath}/blogs`} onClick={() => setOpen(false)} className="text-left text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg px-3 py-2.5 text-base transition-colors">Blog</Link>;
      $[102] = basePath;
      $[103] = blogs.length;
      $[104] = t58;
    } else {
      t58 = $[104];
    }
    let t59;
    if ($[105] !== basePath || $[106] !== rootCategories) {
      let t60;
      if ($[108] !== basePath) {
        t60 = category => category.children && category.children.length > 0 ? <button key={category.id} type="button" onClick={() => pushPanel({
          kind: "category",
          category
        })} className="flex items-center justify-between w-full text-left text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg px-3 py-2.5 text-base transition-colors"><span>{category.name}</span><ChevronRight className="w-4 h-4 text-gray-400" /></button> : <Link key={category.id} href={`${basePath}/c/${category.permalink}`} onClick={() => setOpen(false)} className="text-left text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg px-3 py-2.5 text-base transition-colors">{category.name}</Link>;
        $[108] = basePath;
        $[109] = t60;
      } else {
        t60 = $[109];
      }
      t59 = rootCategories.map(t60);
      $[105] = basePath;
      $[106] = rootCategories;
      $[107] = t59;
    } else {
      t59 = $[107];
    }
    let t60;
    if ($[110] !== basePath || $[111] !== hasAbout) {
      t60 = hasAbout && <Link href={`${basePath}/pages/about-us`} onClick={() => setOpen(false)} className="text-left text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg px-3 py-2.5 text-base transition-colors">About</Link>;
      $[110] = basePath;
      $[111] = hasAbout;
      $[112] = t60;
    } else {
      t60 = $[112];
    }
    const t61 = hasContactPage ? `${basePath}/pages/contact-us` : `${basePath}/#contact`;
    let t62;
    if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
      t62 = () => setOpen(false);
      $[113] = t62;
    } else {
      t62 = $[113];
    }
    let t63;
    if ($[114] !== t) {
      t63 = t("contact");
      $[114] = t;
      $[115] = t63;
    } else {
      t63 = $[115];
    }
    let t64;
    if ($[116] !== t61 || $[117] !== t63) {
      t64 = <Link href={t61} onClick={t62} className="text-left text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg px-3 py-2.5 text-base transition-colors">{t63}</Link>;
      $[116] = t61;
      $[117] = t63;
      $[118] = t64;
    } else {
      t64 = $[118];
    }
    let t65;
    if ($[119] !== t52 || $[120] !== t56 || $[121] !== t57 || $[122] !== t58 || $[123] !== t59 || $[124] !== t60 || $[125] !== t64) {
      t65 = <nav className="flex flex-col gap-1 px-4 flex-1 overflow-y-auto pt-2">{t52}{t56}{t57}{t58}{t59}{t60}{t64}</nav>;
      $[119] = t52;
      $[120] = t56;
      $[121] = t57;
      $[122] = t58;
      $[123] = t59;
      $[124] = t60;
      $[125] = t64;
      $[126] = t65;
    } else {
      t65 = $[126];
    }
    let t66;
    if ($[127] === Symbol.for("react.memo_cache_sentinel")) {
      t66 = () => pushPanel({
        kind: "country"
      });
      $[127] = t66;
    } else {
      t66 = $[127];
    }
    let t67;
    if ($[128] === Symbol.for("react.memo_cache_sentinel")) {
      t67 = <span className="text-gray-400">|</span>;
      $[128] = t67;
    } else {
      t67 = $[128];
    }
    let t68;
    if ($[129] !== currency) {
      t68 = <span>{currency}</span>;
      $[129] = currency;
      $[130] = t68;
    } else {
      t68 = $[130];
    }
    let t69;
    if ($[131] === Symbol.for("react.memo_cache_sentinel")) {
      t69 = <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />;
      $[131] = t69;
    } else {
      t69 = $[131];
    }
    const t70 = <button type="button" onClick={t66} className="flex items-center gap-2 px-4 py-2.5 text-base text-gray-700 hover:bg-gray-50 rounded-lg transition-colors w-full"><span className="text-lg leading-none">{currentRegion?.flag ?? "\uD83C\uDF10"}</span><span className="font-medium">{currentRegion?.label ?? "Region"}</span>{t67}{t68}{t69}</button>;
    const t71 = `${basePath}/account`;
    let t72;
    if ($[132] === Symbol.for("react.memo_cache_sentinel")) {
      t72 = <User className="size-5" />;
      $[132] = t72;
    } else {
      t72 = $[132];
    }
    let t73;
    if ($[133] !== t) {
      t73 = t("myAccount");
      $[133] = t;
      $[134] = t73;
    } else {
      t73 = $[134];
    }
    let t74;
    if ($[135] !== t73) {
      t74 = <span>{t73}</span>;
      $[135] = t73;
      $[136] = t74;
    } else {
      t74 = $[136];
    }
    let t75;
    if ($[137] !== t71 || $[138] !== t74) {
      t75 = <SheetClose asChild={true} className="md:hidden"><Link href={t71} className="flex items-center justify-center gap-2 mx-4 mb-2 px-4 py-3 bg-black text-white rounded-lg text-base font-medium hover:bg-gray-800 transition-colors">{t72}{t74}</Link></SheetClose>;
      $[137] = t71;
      $[138] = t74;
      $[139] = t75;
    } else {
      t75 = $[139];
    }
    let t76;
    if ($[140] !== t70 || $[141] !== t75) {
      t76 = <SheetFooter className="lg:hidden border-t border-gray-200 pt-4 gap-2">{t70}{t75}</SheetFooter>;
      $[140] = t70;
      $[141] = t75;
      $[142] = t76;
    } else {
      t76 = $[142];
    }
    if ($[143] !== t48 || $[144] !== t65 || $[145] !== t76) {
      t7 = <div className={t48}>{t65}{t76}</div>;
      $[143] = t48;
      $[144] = t65;
      $[145] = t76;
      $[146] = t7;
    } else {
      t7 = $[146];
    }
    if ($[147] !== animatedIndex || $[148] !== basePath || $[149] !== panelStack || $[150] !== t) {
      let t77;
      if ($[152] !== animatedIndex || $[153] !== basePath || $[154] !== panelStack.length || $[155] !== t) {
        t77 = (panel_0, index) => {
          if (panel_0.kind !== "category") {
            return null;
          }
          const isAnimatedIn = index <= animatedIndex;
          let translateClass = "translate-x-full";
          if (isAnimatedIn && index < panelStack.length - 1) {
            translateClass = "-translate-x-full";
          } else {
            if (isAnimatedIn) {
              translateClass = "translate-x-0";
            }
          }
          return <div key={`cat-${panel_0.category.id}-${index}`} className={`absolute inset-0 flex flex-col bg-white transition-transform duration-300 ease-in-out ${translateClass}`}><div className="md:hidden px-4 py-2 border-b border-gray-200"><button type="button" onClick={popPanel} className="flex items-center gap-2 text-gray-700 hover:text-gray-900 py-2 text-base font-medium"><ArrowLeft className="w-5 h-5" /><span>{panel_0.category.name}</span></button></div><nav className="flex flex-col gap-1 px-4 flex-1 overflow-y-auto pt-2">{panel_0.category.children?.map(child => child.children && child.children.length > 0 ? <button key={child.id} type="button" onClick={() => pushPanel({
                kind: "category",
                category: child
              })} className="flex items-center justify-between w-full text-left text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg px-3 py-2.5 text-base transition-colors"><span>{child.name}</span><ChevronRight className="w-4 h-4 text-gray-400" /></button> : <Link key={child.id} href={`${basePath}/c/${child.permalink}`} onClick={() => handleOpenChange(false)} className="text-left text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg px-3 py-2.5 text-base transition-colors">{child.name}</Link>)}</nav><div className="border-t border-gray-200 px-4 py-3"><Link href={`${basePath}/c/${panel_0.category.permalink}`} onClick={() => handleOpenChange(false)} className="block w-full text-center text-sm text-gray-500 hover:text-gray-900 py-2 transition-colors">{t("viewAllCategory", {
                  category: panel_0.category.name
                })}</Link></div></div>;
        };
        $[152] = animatedIndex;
        $[153] = basePath;
        $[154] = panelStack.length;
        $[155] = t;
        $[156] = t77;
      } else {
        t77 = $[156];
      }
      t8 = panelStack.map(t77);
      $[147] = animatedIndex;
      $[148] = basePath;
      $[149] = panelStack;
      $[150] = t;
      $[151] = t8;
    } else {
      t8 = $[151];
    }
    const t77 = `absolute inset-0 flex flex-col bg-white transition-transform duration-300 ease-in-out ${currentPanel.kind === "pages" && animatedIndex === panelStack.length - 1 ? "translate-x-0" : "translate-x-full"}`;
    let t78;
    if ($[157] === Symbol.for("react.memo_cache_sentinel")) {
      t78 = <div className="md:hidden px-4 py-2 border-b border-gray-200"><button type="button" onClick={popPanel} className="flex items-center gap-2 text-gray-700 hover:text-gray-900 py-2 text-base font-medium"><ArrowLeft className="w-5 h-5" /><span>Guides</span></button></div>;
      $[157] = t78;
    } else {
      t78 = $[157];
    }
    let t79;
    if ($[158] !== basePath) {
      t79 = page => <Link key={page.slug} href={`${basePath}/pages/${page.slug}`} onClick={() => handleOpenChange(false)} className="text-left text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg px-3 py-2.5 text-base transition-colors">{page.title}</Link>;
      $[158] = basePath;
      $[159] = t79;
    } else {
      t79 = $[159];
    }
    const t80 = guidePages.map(t79);
    let t81;
    if ($[160] !== t80) {
      t81 = <nav className="flex flex-col gap-1 px-4 flex-1 overflow-y-auto py-2">{t80}</nav>;
      $[160] = t80;
      $[161] = t81;
    } else {
      t81 = $[161];
    }
    if ($[162] !== t77 || $[163] !== t81) {
      t9 = <div className={t77}>{t78}{t81}</div>;
      $[162] = t77;
      $[163] = t81;
      $[164] = t9;
    } else {
      t9 = $[164];
    }
    t4 = `absolute inset-0 flex flex-col bg-white transition-transform duration-300 ease-in-out ${currentPanel.kind === "country" && animatedIndex === panelStack.length - 1 ? "translate-x-0" : "translate-x-full"}`;
    let t82;
    if ($[165] === Symbol.for("react.memo_cache_sentinel")) {
      t82 = <ArrowLeft className="w-5 h-5" />;
      $[165] = t82;
    } else {
      t82 = $[165];
    }
    let t83;
    if ($[166] !== t) {
      t83 = t("selectCountry");
      $[166] = t;
      $[167] = t83;
    } else {
      t83 = $[167];
    }
    if ($[168] !== t83) {
      t5 = <div className="md:hidden px-4 py-2 border-b border-gray-200"><button type="button" onClick={popPanel} className="flex items-center gap-2 text-gray-700 hover:text-gray-900 py-2 text-base font-medium">{t82}<span>{t83}</span></button></div>;
      $[168] = t83;
      $[169] = t5;
    } else {
      t5 = $[169];
    }
    t2 = "flex-1 overflow-y-auto px-4 py-2";
    let t84;
    if ($[170] !== country || $[171] !== regionHref) {
      t84 = region => {
        const isSelected = region.country === country.toLowerCase();
        const href = isSelected ? null : regionHref(region);
        const className = `w-full flex items-center gap-3 px-3 py-3 rounded-lg text-base transition-colors ${isSelected ? "bg-gray-100 font-medium" : "hover:bg-gray-50"}`;
        const content = <><span className="text-lg leading-none">{region.flag}</span><span className="flex-1 text-left font-medium">{region.label}</span><span className="text-sm text-gray-500">{region.currency}</span>{isSelected && <Check className="w-4 h-4 text-black" />}</>;
        return href ? <a key={region.host} href={href} onClick={() => setOpen(false)} className={className}>{content}</a> : <div key={region.host} aria-current="true" className={className}>{content}</div>;
      };
      $[170] = country;
      $[171] = regionHref;
      $[172] = t84;
    } else {
      t84 = $[172];
    }
    t3 = regions.map(t84);
    $[1] = animatedIndex;
    $[2] = basePath;
    $[3] = blogs.length;
    $[4] = country;
    $[5] = currency;
    $[6] = hasInteracted;
    $[7] = open;
    $[8] = pages;
    $[9] = panelStack;
    $[10] = regionHref;
    $[11] = rootCategories;
    $[12] = t;
    $[13] = T0;
    $[14] = T1;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
    $[19] = t14;
    $[20] = t15;
    $[21] = t16;
    $[22] = t17;
    $[23] = t18;
    $[24] = t2;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    T0 = $[13];
    T1 = $[14];
    t10 = $[15];
    t11 = $[16];
    t12 = $[17];
    t13 = $[18];
    t14 = $[19];
    t15 = $[20];
    t16 = $[21];
    t17 = $[22];
    t18 = $[23];
    t2 = $[24];
    t3 = $[25];
    t4 = $[26];
    t5 = $[27];
    t6 = $[28];
    t7 = $[29];
    t8 = $[30];
    t9 = $[31];
  }
  let t19;
  if ($[173] !== t2 || $[174] !== t3) {
    t19 = <div className={t2}>{t3}</div>;
    $[173] = t2;
    $[174] = t3;
    $[175] = t19;
  } else {
    t19 = $[175];
  }
  let t20;
  if ($[176] !== t19 || $[177] !== t4 || $[178] !== t5) {
    t20 = <div className={t4}>{t5}{t19}</div>;
    $[176] = t19;
    $[177] = t4;
    $[178] = t5;
    $[179] = t20;
  } else {
    t20 = $[179];
  }
  let t21;
  if ($[180] !== t20 || $[181] !== t6 || $[182] !== t7 || $[183] !== t8 || $[184] !== t9) {
    t21 = <div className={t6}>{t7}{t8}{t9}{t20}</div>;
    $[180] = t20;
    $[181] = t6;
    $[182] = t7;
    $[183] = t8;
    $[184] = t9;
    $[185] = t21;
  } else {
    t21 = $[185];
  }
  let t22;
  if ($[186] !== T0 || $[187] !== t10 || $[188] !== t11 || $[189] !== t12 || $[190] !== t13 || $[191] !== t14 || $[192] !== t15 || $[193] !== t21) {
    t22 = <T0 side={t10} className={t11} showCloseButton={t12} overlayClassName={t13}>{t14}{t15}{t21}</T0>;
    $[186] = T0;
    $[187] = t10;
    $[188] = t11;
    $[189] = t12;
    $[190] = t13;
    $[191] = t14;
    $[192] = t15;
    $[193] = t21;
    $[194] = t22;
  } else {
    t22 = $[194];
  }
  let t23;
  if ($[195] !== T1 || $[196] !== t16 || $[197] !== t17 || $[198] !== t18 || $[199] !== t22) {
    t23 = <T1 open={t16} onOpenChange={t17}>{t18}{t22}</T1>;
    $[195] = T1;
    $[196] = t16;
    $[197] = t17;
    $[198] = t18;
    $[199] = t22;
    $[200] = t23;
  } else {
    t23 = $[200];
  }
  return t23;
}
function _temp5(p_1) {
  return p_1.slug === "contact-us";
}
function _temp4(p_0) {
  return p_0.slug === "about-us";
}
function _temp3(prev_2) {
  return prev_2.length > 1 ? prev_2.slice(0, -1) : prev_2;
}
function _temp2(prev_1) {
  return Math.max(0, prev_1 - 1);
}
function _temp(prev_0) {
  return prev_0 + 1;
}